
import { unlockSaleLine, updateSaleLine } from '@/services/pricingHistories.service';
import VuetifyDataTable from '@/components/VuetifyDataTable.vue';
import AppSelect from '@/components/AppSelect.vue';
import {
  getTotalCost,
  getMaterialMargin,
  getProductMargin,
  canCalculateMargins,
  convertCamelCaseToTitleCase,
  createSaleLineDetails,
  convertEnumToText,
  dateTimeFormat,
} from '@/utils/helpers';
import * as salesOrderLinesServices from '@/services/salesOrderLines.service';
import * as salesServices from '@/services/salesOrder.service';
import { onMounted, ref, computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { dispatchToast } from '@/utils/dispatchToast';
import EventTracker from '@/utils/EventTracker';
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import MultiplePriceListSelector from '@/components/views/saleOrderDetails/MultiplePriceListSelector.vue';
import config from '@/config';
import { useIsMobile } from '@/composables/useIsMobile';
import { useCompactTables } from '@/composables/useCompactTables';
import { erpStatusLabel, needsErpConfirmation } from '@/utils/erpPricingStatus';

export default {
  name: 'SaleOrderDetailsView',
  components: {
    VuetifyDataTable,
    AppSelect,
    ConfirmationModal,
    MultiplePriceListSelector,
  },
  methods: {
    openLink() {
      window.open(this.saleOrderLinesData.saleOrder.url, '_blank');
    },
    handleUpdate({ columnKey, value, item, data }) {
      EventTracker.cellEdit(this.saleOrderLinesData.saleOrder.name, item.partNumber, columnKey);
      // Search the edited item in the rowsData array by id
      const rowUpdated = this.saleOrderLinesData.data.rowsData.find((row) => row.id === item.id);
      // Update the respective item's value
      rowUpdated[columnKey] = value;

      // If we update the suggested price column then we have to recalculate the engine price (only for same base part numbers)
      if (columnKey === 'suggestedPrice') {
        this.onSaleLinesUpdate(true, rowUpdated.partNumber);
      }

      rowUpdated.totalCableCost = getTotalCost(
        data.cableCost,
        data.quotedProductLength,
        rowUpdated.labor,
        rowUpdated.otherCosts,
      );

      if (
        canCalculateMargins(
          rowUpdated.suggestedPrice || rowUpdated.enginePrice,
          rowUpdated.totalCableCost,
          rowUpdated.laborCost,
          rowUpdated.otherCosts,
        )
      ) {
        const materialMargin = getMaterialMargin(
          Number(rowUpdated.suggestedPrice || rowUpdated.enginePrice),
          rowUpdated.totalCableCost ? Number(rowUpdated.totalCableCost) : 0,
          Number(rowUpdated.otherCosts || 0),
        );
        const productMargin = getProductMargin(
          Number(rowUpdated.suggestedPrice || rowUpdated.enginePrice),
          rowUpdated.totalCableCost ? Number(rowUpdated.totalCableCost) : 0,
          Number(rowUpdated.otherCosts || 0),
          Number(rowUpdated.labor || 0),
        );
        rowUpdated.materialMargin = materialMargin;
        rowUpdated.productMargin = productMargin;
      } else {
        rowUpdated.materialMargin = null;
        rowUpdated.productMargin = null;
      }
    },
    async wrappedUpdateSaleLine(id, body) {
      const bodyCopy = { ...body };
      bodyCopy.laborCost = bodyCopy.labor;
      delete bodyCopy.labor;
      // Convert empty suggestedPrice to null
      if ('suggestedPrice' in bodyCopy && (bodyCopy.suggestedPrice === '' || bodyCopy.suggestedPrice === undefined)) {
        bodyCopy.suggestedPrice = null;
      }
      return await updateSaleLine(id, bodyCopy);
    },
  },
  computed: {
    showConfirmationModalComputed() {
      return this.showConfirmationModal;
    },
  },
  data() {
    return {
      unlockConfirmationMessage: 'Unlocking this price will make it editable again. Are you sure you want to continue?',
      unlockConfirmationTitle: 'Unlock Price Confirmation',
      unlockConfirmButtonText: 'Unlock Price',
      unlockCancelButtonText: 'Cancel',
    };
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const saleOrderId: string = route.params.saleOrderId as string;

    // Get refresh params from the query string if set, and remove them
    const refresh: boolean = route.query.refresh === 'true' ? true : route.query.refresh === 'false' ? false : null;
    const name = route.query.name;
    router.replace({ query: { ...route.query, refresh: undefined, name: undefined } });

    const saleOrderStatesSelectOptions = reactive({
      isLoading: false,
      states: [],
    });

    const saleOrderPricingStatus = ref(null);
    const saleOrderLinesData: any = reactive({
      isLoading: false,
      error: '',
      data: {},
      saleOrder: {},
      customer: {},
    });

    const multiplePriceListIds = ref(new Set<string>());
    const priceListNames = ref<string[]>([]);

    // Multiple pricelists tracking
    const saleLinesWithMultiplePriceLists = ref<any[]>([]);
    const pricesSelected = ref(0);
    const pricesNeeded = ref(0);
    const showPriceListSelector = ref(false);

    const showConfirmationModal = ref(false);
    const isUpdatingHistoryExclusion = ref(false);
    const showActionsMenu = ref(false);
    const showResetPricesModal = ref(false);
    const showUnlockConfirmationModal = ref(false);
    const currentUnlockItem = ref(null);

    const loadingState = reactive({
      isLoading: false,
      message: '',
    });

    // Mobile "limited view" of the line items table: only what's needed to
    // price on a phone (part number, suggested price, price in Odoo with its
    // lock). Qty, costs and notes stay desktop-only — tilting the phone to
    // landscape brings the full column set back on any phone (useCompactTables
    // is portrait-only; the table then scrolls horizontally).
    // Part number gets the width freed by the dropped columns (it's the long
    // one); the rest shrink so horizontal scrolling stays minimal.
    const isMobile = useIsMobile();
    const compactTables = useCompactTables();
    const mobileHiddenColumns = ['uomQty', 'odooCost', 'notes'];
    // Set as BOTH width and minWidth: a bare `width` is only a hint the
    // browser shrinks away when the table is tight (which it always is on a
    // phone), so these were silently ignored. minWidth is a hard floor — the
    // table overflows into horizontal scroll instead of squeezing columns.
    const mobileColumnWidths: Record<string, number> = {
      partNumber: 200,
      suggestedPrice: 160,
      odooPrice: 140,
    };
    const visibleColumnsDefs = computed(() => {
      const columnsDefs = saleOrderLinesData.data?.columnsDefs || [];
      if (!compactTables.value) return columnsDefs;
      return columnsDefs
        .filter((columnDef) => !mobileHiddenColumns.includes(columnDef.key))
        .map((columnDef) =>
          columnDef.key in mobileColumnWidths
            ? {
                ...columnDef,
                width: mobileColumnWidths[columnDef.key],
                minWidth: `${mobileColumnWidths[columnDef.key]}px`,
              }
            : columnDef,
        );
    });

    // Facts-panel entries. Phones merge Customer + End User into one item
    // ("AWS (AWS)") so Order Date fits on the same grid row — one row less,
    // more vertical room for the table. Desktop keeps all five separate.
    // Odoo's own state of the quote. Independent of our `pricingStatus`, and
    // absent until the backend exposes it — `needsErpConfirmation` treats that
    // absence as "open for pricing", so behaviour is unchanged without it.
    const erpStatusIsPastDraft = computed(() => needsErpConfirmation(saleOrderLinesData.saleOrder?.erpPricingStatus));
    const erpStatusText = computed(() => erpStatusLabel(saleOrderLinesData.saleOrder?.erpPricingStatus));

    const soFactsComputed = computed(() => {
      const customer = saleOrderLinesData.customer.name || 'N/A';
      const endUser = saleOrderLinesData.saleOrder.endUser || 'N/A';
      // Odoo's "End User Order" (Oracle's program code, AWS's FBN) rides on the End User
      // fact as a barcode badge with the value in its tooltip: the strip is full already.
      // Desktop only for now, the phone grid merges Customer + End User.
      const endUserOrder = saleOrderLinesData.saleOrder.endUserOrder || '';
      const endUserBadge = endUserOrder ? { badge: { icon: 'mdi-barcode', text: endUserOrder } } : {};
      const segments = saleOrderLinesData.customer.relationshipSegment?.join(', ') || 'N/A';
      const relationship = saleOrderLinesData.customer.relationshipType || 'N/A';
      const orderDate = saleOrderLinesData.saleOrder.orderDate
        ? dateTimeFormat(saleOrderLinesData.saleOrder.orderDate, 'MMMM DD, YYYY')
        : '';
      // Odoo's own state of the quote, mirrored from the ERP. Omitted rather
      // than shown as N/A when the backend does not send it, so the row never
      // claims a state we do not actually know.
      const odooStatus = erpStatusText.value;

      if (isMobile.value) {
        // The phone grid has four cells, so the Odoo state rides along with
        // the order date in parentheses, the same pairing trick the customer
        // and end user already use. Each half stands alone when the other is
        // missing, so the label never promises a value that is not there.
        const dateAndStatus =
          orderDate && odooStatus
            ? [{ label: 'Order Date (Status)', value: `${orderDate} (${odooStatus})` }]
            : orderDate
              ? [{ label: 'Order Date', value: orderDate }]
              : odooStatus
                ? [{ label: 'Odoo Status', value: odooStatus }]
                : [];

        return [
          { label: 'Customer (End User)', value: `${customer} (${endUser})` },
          ...dateAndStatus,
          { label: 'Segments', value: segments },
          { label: 'Relationship', value: relationship },
        ];
      }

      return [
        { label: 'Customer', value: customer },
        { label: 'End User', value: endUser, ...endUserBadge },
        { label: 'Segments', value: segments },
        { label: 'Relationship', value: relationship },
        ...(orderDate ? [{ label: 'Order Date', value: orderDate }] : []),
        ...(odooStatus ? [{ label: 'Odoo Status', value: odooStatus }] : []),
      ];
    });

    // ========================= Lifecycle hooks =========================
    onMounted(async () => {
      loadingState.isLoading = true;
      loadingState.message = 'Searching for Results...';
      EventTracker.saleOrderView();
      await getSaleOrderStates();

      if (refresh === true && name) {
        // If refresh is true and name is set, then the parent has determined it needs refresh, therefore we save one request
        saleOrderLinesData.saleOrder.name = name;
        await onSaleLinesUpdate(false);
      } else {
        await getSaleLinesBySaleOrderId();
      }
      loadingState.isLoading = false;
    });

    // ========================= Computed =========================
    const isLoadingComputed = computed(() => loadingState.isLoading);

    const postConfirmation = computed(() =>
      erpStatusIsPastDraft.value
        ? {
            title: 'Post prices anyway?',
            message:
              `This order is already marked "${erpStatusText.value}" in Odoo, so Odoo will probably reject the ` +
              'prices posted from here. Posting overwrites the prices of the sale order lines in Odoo.',
            confirmText: 'Post anyway',
            cancelText: 'Cancel',
          }
        : {
            title: 'Are you sure you want to post prices to Odoo?',
            message: 'Posting will overwrite the prices of the sales order lines in Odoo',
            confirmText: 'Post to Odoo',
            cancelText: 'Continue Pricing',
          },
    );

    // ========================= Methods =========================
    const onSaleLinesUpdate = async (onlyEnginePrice = false, partNumber = null) => {
      loadingState.isLoading = true;
      loadingState.message = onlyEnginePrice ? 'Recalculating engine prices...' : 'Refreshing data from Odoo...';

      const { success, data } = onlyEnginePrice
        ? await salesServices.refreshSaleOrderPrices(saleOrderId, partNumber)
        : await salesServices.syncSalesOrderLinesBySaleOrderName(saleOrderLinesData.saleOrder.name);
      if (!success) {
        dispatchToast(data.message, {
          type: 'error',
          timeout: false,
          draggable: false,
          closeOnClick: false,
        });
      } else {
        dispatchToast(`Sale Lines Updated Successfully!`, {
          type: 'success',
        });
        loadingState.message = 'Loading Sale Lines...';
        await getSaleLinesBySaleOrderId();
      }

      EventTracker.rfqLoad(true);
      loadingState.isLoading = false;
    };

    // Optimistic: the switch moves at once and rolls back if the write
    // fails, because the menu stays open and a switch that lags its own click
    // reads as broken. The server's value wins over ours on success.
    const onExcludeFromHistoryChange = async (value) => {
      const previous = Boolean(saleOrderLinesData.saleOrder.excludeFromHistory);
      saleOrderLinesData.saleOrder.excludeFromHistory = value;
      isUpdatingHistoryExclusion.value = true;

      const { success, data } = await salesServices.updateSaleOrder(saleOrderId, { excludeFromHistory: value });

      if (success) {
        saleOrderLinesData.saleOrder.excludeFromHistory = data?.excludeFromHistory ?? value;
        dispatchToast(
          value ? 'This order will be skipped for price suggestions.' : 'This order will be used for price suggestions.',
          { type: 'success' },
        );
      } else {
        saleOrderLinesData.saleOrder.excludeFromHistory = previous;
        dispatchToast(data?.message || 'Unable to update the price suggestion setting.', { type: 'error' });
      }

      isUpdatingHistoryExclusion.value = false;
    };

    // Every line the grid holds, which is every active line of the order: the
    // detail endpoint already leaves out deleted and child lines.
    const resettableLines = computed(() => saleOrderLinesData.data?.rowsData ?? []);
    const manuallyPricedCount = computed(
      () => resettableLines.value.filter((line) => line.pricingInfo?.priceSource === 'manual').length,
    );

    // The count of hand-priced lines is the point of the dialog, so it is in
    // the sentence either way; the warning only follows when there is
    // something to lose.
    const resetPricesConfirmation = computed(() => {
      const lines = resettableLines.value.length;
      const byHand = manuallyPricedCount.value;
      const counts = `${lines} line${lines === 1 ? '' : 's'}, ${byHand > 0 ? `${byHand} of them` : 'none'} priced by hand.`;

      return {
        title: 'Reset every price on this order?',
        message: `Every price on this order is cleared and calculated again from scratch: ${counts}${
          byHand > 0 ? ' A price typed by hand cannot be recovered.' : ''
        }`,
        confirmText: 'Reset all prices',
        cancelText: 'Cancel',
      };
    });

    const onResetAllPricesClick = () => {
      // The panel does not close on its own — :close-on-content-click="false"
      // is there for the toggle — and a dialog opening behind an open panel
      // reads as a misfire.
      showActionsMenu.value = false;
      showResetPricesModal.value = true;
    };

    const onResetAllPricesConfirmation = {
      async confirm() {
        showResetPricesModal.value = false;

        const lines = resettableLines.value.slice();
        let reset = 0;
        let failed = 0;

        loadingState.isLoading = true;

        // One line at a time, the same pacing as the price-list apply flow:
        // each reset runs the engine server-side, so firing them together
        // would only move the queue into the backend. A failure does not stop
        // the rest — an order abandoned halfway is worse than one fully reset
        // bar the lines that would not take it, and the toast says how many.
        for (const line of lines) {
          loadingState.message = `Resetting prices (${reset + failed + 1} of ${lines.length})...`;
          const { success } = await updateSaleLine(line.id, { suggestedPrice: null });
          if (success) reset += 1;
          else failed += 1;
        }

        if (reset > 0) {
          loadingState.message = 'Loading Sale Lines...';
          await getSaleLinesBySaleOrderId();
        }

        loadingState.isLoading = false;

        if (failed === 0) {
          dispatchToast(`${reset} price${reset === 1 ? '' : 's'} reset and recalculated.`, { type: 'success' });
        } else if (reset > 0) {
          dispatchToast(`${reset} price${reset === 1 ? '' : 's'} reset, ${failed} failed.`, { type: 'warning' });
        } else {
          dispatchToast('Unable to reset the prices on this order.', { type: 'error' });
        }
      },
      cancel() {
        showResetPricesModal.value = false;
      },
    };

    const onConfirmationModalEvents = {
      async confirm() {
        loadingState.isLoading = true;
        loadingState.message = 'Posting Prices to Odoo...';
        showConfirmationModal.value = false;

        const { success, data } = await salesOrderLinesServices.sendSalesOrderLinesToOdoo(
          saleOrderLinesData.data.rowsData,
          saleOrderLinesData.saleOrder.id,
        );
        EventTracker.postPrices(saleOrderLinesData.saleOrder.name);
        if (success) {
          dispatchToast('Prices posted to Odoo!', { type: 'success' });
          await onSaleLinesUpdate(false);
        } else {
          dispatchToast('Unable to post prices to Odoo!', { type: 'error' });
          loadingState.isLoading = false;
        }
      },
      cancel() {
        showConfirmationModal.value = false;
      },
    };

    const getSaleOrderStates = async () => {
      const { success, data } = await salesServices.getSaleOrderStates();
      if (success) {
        saleOrderStatesSelectOptions.states = data.map((state) => {
          return { name: convertEnumToText(state), value: state };
        });
      } else {
        dispatchToast('Unable to get the Sale Order States!', {
          type: 'error',
        });
      }
    };

    const getSaleLinesBySaleOrderId = async () => {
      const { success, data } = await salesServices.getSaleOrderDetails(saleOrderId);

      if (!success) {
        saleOrderLinesData.error = data.message;
      } else {
        const saleOrder = data;
        saleOrderLinesData.data = formatSaleLineDetailsForVuetifyDataTable(saleOrder.saleLines);

        saleOrderPricingStatus.value = saleOrder.pricingStatus;
        saleOrderLinesData.saleOrder = saleOrder;
        saleOrderLinesData.saleOrder.url = `${config.baseUrls.erp}/web?#id=${saleOrder.erpId}&action=484&model=sale.order&view_type=form&cids=1&menu_id=300`;
        saleOrderLinesData.customer = saleOrder.partner;

        // Collect all unique pricelist IDs, names, and track counts
        let selected = 0;
        let needed = 0;
        const namesSet = new Set<string>();
        const linesWithMultiple: any[] = [];

        saleOrder.saleLines.forEach((saleLine) => {
          if (saleLine.multiplePriceListIds?.length) {
            saleLine.multiplePriceListIds.forEach((priceListId) => {
              multiplePriceListIds.value.add(priceListId);
            });

            // Collect pricelist names and tags from multiplePriceLists
            const multiplePriceLists =
              saleLine.multiplePriceLists?.map((item: { priceList: { id: string; name: string; tag?: string }; enginePrice: number }) => ({
                priceListId: item.priceList?.id,
                priceListName: item.priceList?.name,
                priceListTag: item.priceList?.tag || '',
                enginePrice: item.enginePrice,
              })) || [];

            multiplePriceLists.forEach((pl: { priceListName: string; priceListTag?: string }) => {
              if (pl.priceListName) {
                // Use tag if available, otherwise fall back to name
                const displayLabel = pl.priceListTag || pl.priceListName;
                namesSet.add(displayLabel);
              }
            });

            // Add to lines with multiple pricelists (formatted for selector)
            linesWithMultiple.push({
              id: saleLine.id,
              partNumber: saleLine.customerDefaultCode,
              currentSuggestedPrice: saleLine.suggestedPrice || null,
              currentPriceListId: saleLine.priceListId || null,
              multiplePriceLists,
            });

            // Track selected vs needed
            if (saleLine.priceListId || saleLine.suggestedPrice) {
              selected++;
            } else {
              needed++;
            }
          }
        });

        saleLinesWithMultiplePriceLists.value = linesWithMultiple;
        pricesSelected.value = selected;
        pricesNeeded.value = needed;
        priceListNames.value = Array.from(namesSet);
      }
    };

    const onMultiplePricelistsBannerClick = () => {
      showPriceListSelector.value = true;
    };

    const onPriceListSelectionConfirm = async (
      selections: Array<{
        lineId: string;
        type: 'pricelist' | 'manual';
        priceListId?: string;
        price?: number;
        manualPrice?: string;
      }>,
    ) => {
      loadingState.isLoading = true;
      loadingState.message = 'Applying price selections...';

      let successCount = 0;
      let failedCount = 0;

      try {
        for (const selection of selections) {
          const lineToUpdate = saleOrderLinesData.data.rowsData.find((row: any) => row.id === selection.lineId);
          if (!lineToUpdate) continue;

          let updateBody: any = {};

          if (selection.type === 'pricelist' && selection.priceListId) {
            updateBody = {
              priceListId: selection.priceListId,
              enginePrice: selection.price,
              // suggestedPrice: selection.price,
            };
          } else if (selection.type === 'manual' && selection.manualPrice) {
            updateBody = {
              suggestedPrice: parseFloat(selection.manualPrice),
              priceListId: null, // Clear pricelist selection for manual price
            };
          }

          if (Object.keys(updateBody).length > 0) {
            const { success } = await updateSaleLine(selection.lineId, updateBody);
            if (success) {
              successCount++;
              // Update local data
              if (updateBody.suggestedPrice !== undefined) {
                lineToUpdate.suggestedPrice = updateBody.suggestedPrice;
              }
              if (updateBody.priceListId !== undefined) {
                lineToUpdate.priceListId = updateBody.priceListId;
              }
            } else {
              failedCount++;
            }
          }
        }

        // Show appropriate toast based on results
        if (failedCount === 0 && successCount > 0) {
          dispatchToast('Price selections applied successfully!', { type: 'success' });
        } else if (failedCount > 0 && successCount > 0) {
          dispatchToast(`${successCount} price(s) updated, ${failedCount} failed`, { type: 'warning' });
        } else if (failedCount > 0 && successCount === 0) {
          dispatchToast('Failed to apply price selections', { type: 'error' });
        }

        // Refresh the data to update counts and display
        // await getSaleLinesBySaleOrderId();
        await onSaleLinesUpdate(true);
      } catch (error) {
        console.error('Error applying price selections:', error);
        dispatchToast('Failed to apply price selections', { type: 'error' });
      } finally {
        loadingState.isLoading = false;
      }
    };

    // Route to the search + sticky-bottom (simulator) view for a given line,
    // carrying the full sale-order context.
    const getSearchRoute = (item) => ({
      path: '/home/search',
      query: {
        pn: item.partNumber,
        customerId: saleOrderLinesData.customer.id?.toString(),
        saleOrderId: saleOrderId,
        saleOrderErpId: saleOrderLinesData.saleOrder.erpId?.toString(),
        saleOrderName: saleOrderLinesData.saleOrder.name,
        saleLineId: item.id?.toString(),
      },
    });

    // Resolved href for the part-number cell anchor, so Cmd/Ctrl-click opens
    // the search view in a new tab natively.
    const getSearchLineHref = (item) => router.resolve(getSearchRoute(item)).href;

    // The bordered page container (and its section title) is gone, so the
    // back arrow in the header is the way home to the RFQs list.
    const goToRfqs = () => router.push({ name: 'sale-orders' });

    // Open the search view in-app. Shared by the part-number cell click and the
    // suggested-price editor's "search and select reference lines" link.
    const goToSearch = (item) => {
      if (!item) return;
      EventTracker.orderLineClick({
        partNumber: item.partNumber,
        customer: saleOrderLinesData.customer.name,
      });
      router.push(getSearchRoute(item));
    };

    const onCellClick = (event) => {
      if (event?.colDef?.field === 'partNumber') {
        goToSearch(event?.data);
      }
    };

    const onChangePricingStatus = async (event) => {
      const newPricingStatus = event.target.value;
      saleOrderPricingStatus.value = newPricingStatus;

      loadingState.isLoading = true;
      loadingState.message = 'Updating pricing status...';

      const { success, data } = await salesServices.updateSaleOrder(saleOrderId, {
        pricingStatus: newPricingStatus,
      });

      if (!success) {
        dispatchToast(data.message, { type: 'error' });
      }

      loadingState.isLoading = false;
    };

    const formatSaleLineDetailsForVuetifyDataTable = (records: any[]) => {
      if (!records.length) return {};

      const columnsToShow = [
        'partNumber',
        'familyCode',
        'uomQty',
        'suggestedPrice',
        'odooPrice',
        'odooCost',
        // 'totalCost',
        'notes',
      ];

      const formattedTable: FormattedSaleLineDetailsDataForVuetifyDataTable = {
        columnsDefs: [],
        rowsData: [],
      };

      function createVuetifyColumnDef(colName: string): VuetifyDataTableHeader {
        const currentColDef: VuetifyDataTableHeader = {
          title: convertCamelCaseToTitleCase(colName),
          key: colName,
        };
        switch (colName) {
          case 'partNumber':
            // minWidth is a hard floor (a bare `width` gets shrunk by auto
            // table layout): even the extra-long PNs must land on ONE line,
            // including the 72px group-alignment indent — the other columns
            // just get pushed right. Still (barely) fits ~1280px laptops.
            currentColDef['width'] = 500;
            currentColDef['minWidth'] = '500px';
            break;
          case 'uomQty':
            currentColDef['title'] = 'Qty';
            // Capped: qty is a short number, spare table width should go to
            // the flexible columns instead.
            currentColDef['width'] = 80;
            currentColDef['maxWidth'] = '110px';
            break;
          case 'familyCode':
            currentColDef['cellClass'] = 'vp-force-hidden';
            break;
          case 'suggestedPrice':
            currentColDef['formatter'] = 'currency';
            currentColDef['fallbackColumn'] = 'enginePrice';
            currentColDef['fallbackTooltip'] = 'enginePriceTooltip';
            // Floor sized for the wide "price → formula" pricing layout
            // (mobile overrides this with its own tighter widths).
            currentColDef['width'] = 240;
            currentColDef['minWidth'] = '240px';
            break;
          case 'odooPrice':
            currentColDef['title'] = 'Price in Odoo';
            currentColDef['formatter'] = 'currency';
            currentColDef['width'] = 180;
            break;
          case 'notes':
            // Notes are almost always empty, so keep this column slim.
            currentColDef['width'] = 220;
            currentColDef['sortable'] = false;
            break;
          case 'totalCost':
            currentColDef['formatter'] = 'currency';
            currentColDef['width'] = 140;
            break;
        }
        return currentColDef;
      }

      const columnsDefs: VuetifyDataTableHeader[] = [];
      columnsToShow.forEach((colName: string) => {
        const currentColDef: VuetifyDataTableHeader = createVuetifyColumnDef(colName);
        columnsDefs.push(currentColDef);
      });

      // Format Rows
      const rowsData: SaleLineDetailsForDataTable[] = [];

      records.forEach((record: any) => {
        const row: SaleLineDetailsForDataTable = createSaleLineDetails(record);
        rowsData.push(row);
      });

      formattedTable['columnsDefs'] = columnsDefs;
      formattedTable['rowsData'] = rowsData;

      return formattedTable;
    };

    // Handle unlocking a price
    const showUnlockPriceConfirmation = (item) => {
      currentUnlockItem.value = item;
      showUnlockConfirmationModal.value = true;
    };

    const onUnlockConfirmation = {
      async confirm() {
        if (!currentUnlockItem.value) return;

        loadingState.isLoading = true;
        loadingState.message = 'Unlocking price...';

        const itemId = currentUnlockItem.value.id;
        const { success, data } = await unlockSaleLine(itemId);
        showUnlockConfirmationModal.value = false;

        if (success) {
          // Find the lock icon before updating the data model
          const lockIcon = document.querySelector(`[data-id="${itemId}"]`);

          if (lockIcon) {
            loadingState.isLoading = false;

            // Unlock animation
            lockIcon.classList.remove('mdi-lock');
            lockIcon.classList.add('mdi-lock-open-outline');
            lockIcon.classList.add('unlock-animation');

            // Wait for animation to complete before hiding the icon
            setTimeout(() => {
              // Now update the data model
              const itemToUpdate = saleOrderLinesData.data.rowsData.find((row) => row.id === itemId);
              if (itemToUpdate) {
                itemToUpdate.isPriced = false;
              }
              lockIcon.classList.add('vp-force-hidden');
              dispatchToast('Price unlocked successfully!', { type: 'success' });
            }, 1100);
          } else {
            // If we can't find the lock icon, just update the data
            const itemToUpdate = saleOrderLinesData.data.rowsData.find((row) => row.id === itemId);
            if (itemToUpdate) {
              itemToUpdate.isPriced = false;
            }
            dispatchToast('Price unlocked successfully!', { type: 'success' });
            loadingState.isLoading = false;
          }
        } else {
          dispatchToast(`Failed to unlock price: ${data.message || 'Unknown error'}`, { type: 'error' });
          loadingState.isLoading = false;
        }

        currentUnlockItem.value = null;
      },
      cancel() {
        showUnlockConfirmationModal.value = false;
        currentUnlockItem.value = null;
      },
    };

    return {
      showConfirmationModal,
      showUnlockConfirmationModal,
      isUpdatingHistoryExclusion,
      onExcludeFromHistoryChange,
      showActionsMenu,
      showResetPricesModal,
      resettableLines,
      resetPricesConfirmation,
      onResetAllPricesClick,
      onResetAllPricesConfirmation,
      postConfirmation,
      saleOrderPricingStatus,
      isLoadingComputed,
      saleOrderLinesData,
      visibleColumnsDefs,
      soFactsComputed,
      saleOrderStatesSelectOptions,
      loadingState,
      onConfirmationModalEvents,
      onUnlockConfirmation,
      onCellClick,
      goToSearch,
      goToRfqs,
      getSearchLineHref,
      onChangePricingStatus,
      onSaleLinesUpdate,
      showUnlockPriceConfirmation,
      formatSaleLineDetailsForVuetifyDataTable,
      // Multiple pricelists feature
      multiplePriceListIds,
      priceListNames,
      onMultiplePricelistsBannerClick,
      saleLinesWithMultiplePriceLists,
      pricesSelected,
      pricesNeeded,
      showPriceListSelector,
      onPriceListSelectionConfirm,
    };
  },
};

interface FormattedSaleLineDetailsDataForVuetifyDataTable {
  columnsDefs: VuetifyDataTableHeader[];
  rowsData: SaleLineDetailsForDataTable[];
}

interface SaleLineDetailsForDataTable {
  partNumber: string;
  partNumberBase: string;
  familyCode: string;
  uomQty: string;
  pricingType: string;
  materialMargin: number | null;
  productMargin: number | null;
  suggestedPrice: string;
  enginePrice: string | null;
  enginePriceTooltip: string | null;
  odooCost: string;
  notes: string;
  customerId: string;
  id: string;
  erpId: string;
  isHigherLengthThanEverSold: boolean;
}

interface VuetifyDataTableHeader {
  key: string;
  title: string;
  colspan?: number;
  rowspan?: number;
  fixed?: boolean;
  align?: 'start' | 'end' | 'center';
  width?: number;
  minWidth?: string;
  maxWidth?: string;
  sortable?: boolean;
  cellClass?: string | string[];
  formatter?: string;
  fallbackColumn?: string;
  fallbackTooltip?: string;
}

export type OdooStatus =
  | 'cancel'
  | 'done'
  | 'draft'
  | 'draft_quote'
  | 'inv'
  | 'lost'
  | 'partial'
  | 'pi'
  | 'sale'
  | 'sent'
  | 'shipped';
